import { useCallback, useMemo } from 'react';

import { BankAccountsModel, OutletModel } from '@pukka/api';
import { Input, SelectV2, Text } from '@pukka/ui-components';
import { useSnapshot } from 'valtio';

import { FormInput } from './FormInput';
import { GeneralFormState, onChangeValue, ValidationState } from './GopayActivationForm.state';

interface GeneralFormProps {
  readonly outlets: OutletModel.Outlet[];
  readonly bankList: BankAccountsModel.BankList;
  readonly GeneralState?: typeof GeneralFormState;
  readonly Validation?: typeof ValidationState;
}

function GeneralForm({ outlets, bankList, GeneralState = GeneralFormState, Validation = ValidationState }: GeneralFormProps) {
  const { dirty, outletIds, phone, bankName, accountNumber, accountOwner, businessType } = useSnapshot(GeneralState);
  const { generalValidation } = useSnapshot(Validation);

  const outletOptions = useMemo(() => {
    return outlets.map((o) => {
      return {
        key: o.id,
        value: o.name,
      };
    });
  }, [outlets]);

  const bankOptions = useMemo(() => {
    return bankList.map((bank) => {
      return {
        key: bank.name,
        value: bank.name,
      };
    });
  }, [bankList]);

  const onChangeOutlet = useCallback((_outletIds: number[]) => {
    onChangeValue('general', 'outletIds')(_outletIds ?? []);
  }, []);

  return (
    <div>
      <Text as="h3" variant="title-2" weight="bold" className="mb-4" color="blue">
        Data Umum Merchant
      </Text>
      <FormInput
        required
        label="Nama Outlet Merchant"
        description="Isi dengan nama per-outletnya/cabang."
        error={dirty.has('outletIds') && !generalValidation.outletIds.success}
      >
        <SelectV2
          className="max-w-[calc(100vw-108px)] md:max-w-[600px]"
          multiple
          placeholder="Pilih outlet"
          options={outletOptions}
          value={outletIds as number[]}
          onChange={onChangeOutlet}
        />
      </FormInput>
      <FormInput
        required
        label="Nomor Telepon PIC Merchant"
        description="Nomor yang di cantumkan WAJIB menggunakan Nomor Whatsapp."
        error={dirty.has('phone') && !generalValidation.phone.success && generalValidation.phone.error}
      >
        <Input type="tel" value={phone} maxLength={15} onChangeValue={onChangeValue('general', 'phone')} placeholder="Nomor telepon" />
      </FormInput>
      <FormInput
        required
        label="Nama Bank"
        description="Input nama bank yang didaftarkan untuk aktivasi GoPay"
        error={dirty.has('bankName') && !generalValidation.bankName.success && generalValidation.bankName.error}
      >
        <SelectV2 placeholder="Nama bank" options={bankOptions} value={bankName} onChange={onChangeValue('general', 'bankName')} />
      </FormInput>
      <FormInput
        required
        label="Nomor Rekening"
        description="Input nomor rekening sesuai dengan Nama Bank yang di isi."
        error={dirty.has('accountNumber') && !generalValidation.accountNumber.success && generalValidation.accountNumber.error}
      >
        <Input
          type="tel"
          value={accountNumber}
          maxLength={30}
          onChangeValue={onChangeValue('general', 'accountNumber')}
          placeholder="Nomor rekening"
        />
      </FormInput>
      <FormInput
        required
        label="Nama Pemilik Rekening"
        error={dirty.has('accountOwner') && !generalValidation.accountOwner.success && generalValidation.accountOwner.error}
      >
        <Input
          type="text"
          value={accountOwner}
          maxLength={100}
          onChangeValue={onChangeValue('general', 'accountOwner')}
          placeholder="Pemilik rekening"
        />
      </FormInput>
      <FormInput
        required
        label="Jenis Usaha"
        error={dirty.has('businessType') && !generalValidation.businessType.success && generalValidation.businessType.error}
      >
        <SelectV2
          placeholder="Jenis usaha"
          value={businessType}
          onChange={onChangeValue('general', 'businessType') as (value: string | number | boolean) => void}
        >
          <SelectV2.Option value="individual">Individu</SelectV2.Option>
          <SelectV2.Option value="business">Badan Usaha</SelectV2.Option>
        </SelectV2>
      </FormInput>
    </div>
  );
}

export { GeneralForm };
